import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminButton, AdminCard, AdminCardHeader, AdminErrorBanner, AdminFormField, AdminPageLayout, AdminPagination, AdminSelect, AdminStatusBadge, AdminTable, AdminTd, AdminTr } from "@/components/admin";
import { fetchAdminEafrEvents, type AdminEafrEventListItem } from "@/lib/api";
import { formatDateTime, formatNullable } from "@/lib/format";

const tone = (status: string) => status === "COMPLETE" || status === "VERIFIED" ? "success" : status === "FAILED" ? "danger" : status === "PARTIAL" || status === "PENDING" ? "warning" : "neutral";

const EafrSoundEventsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AdminEafrEventListItem[]>([]);
  const [analysisStatusCode, setAnalysisStatusCode] = useState("");
  const [needsAttentionYn, setNeedsAttentionYn] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const size = 20;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await fetchAdminEafrEvents({
        analysisStatusCode: analysisStatusCode ? analysisStatusCode as "COMPLETE" | "PARTIAL" | "FAILED" : undefined,
        needsAttentionYn: needsAttentionYn ? needsAttentionYn as "Y" | "N" : undefined,
        page: page - 1, size,
      });
      setItems(result.items); setTotalElements(result.totalElements); setTotalPages(Math.max(1, result.totalPages));
    } catch (cause) {
      setItems([]); setTotalElements(0); setTotalPages(1);
      setError(cause instanceof Error ? cause.message : "EAFR 이벤트를 불러오지 못했습니다.");
    } finally { setLoading(false); }
  }, [analysisStatusCode, needsAttentionYn, page]);

  useEffect(() => { void load(); }, [load]);

  return <AdminPageLayout title="EAFR 분석 이벤트" subtitle="현재 ELDER_FULL · EAFR-EVENT-v1 실행 결과를 조회합니다." actions={<AdminButton variant="primary" size="sm" icon={loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} onClick={() => void load()} disabled={loading}>새로고침</AdminButton>}>
    <AdminErrorBanner message={error} />
    <AdminCard className="mb-4 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <AdminFormField label="분석 상태"><AdminSelect value={analysisStatusCode} onValueChange={(value) => { setAnalysisStatusCode(value); setPage(1); }}><option value="">전체</option><option value="COMPLETE">완료</option><option value="PARTIAL">부분 완료</option><option value="FAILED">실패</option></AdminSelect></AdminFormField>
        <AdminFormField label="주의 필요"><AdminSelect value={needsAttentionYn} onValueChange={(value) => { setNeedsAttentionYn(value); setPage(1); }}><option value="">전체</option><option value="Y">주의 필요</option><option value="N">일반</option></AdminSelect></AdminFormField>
      </div>
    </AdminCard>
    <AdminCard>
      <AdminCardHeader title="최신 EAFR 이벤트" action={<span className="text-xs text-slate-400">총 {totalElements}건</span>} />
      <AdminTable headers={["분석 시각", "Senior·기기", "상황", "분석 상태", "구성요소", "증적", "상세"]} loading={loading} emptyMessage={!loading && items.length === 0 ? "조회된 EAFR 이벤트가 없습니다." : undefined}>
        {items.map((item) => <AdminTr key={item.soundAnalysisEventId} className="hover:bg-slate-50">
          <AdminTd><div className="text-xs font-semibold text-slate-700">{formatDateTime(item.analyzedAt)}</div><div className="mt-1 text-xs text-slate-400">#{item.soundAnalysisEventId}</div></AdminTd>
          <AdminTd><div className="text-sm font-semibold text-slate-800">{formatNullable(item.seniorDisplayName)}</div><div className="mt-1 text-xs text-slate-400">{formatNullable(item.deviceName)}</div></AdminTd>
          <AdminTd><div className="text-sm text-slate-700">{formatNullable(item.primarySituationCode)}</div><div className="mt-1 text-xs text-slate-400">주의 {item.needsAttentionYn}</div></AdminTd>
          <AdminTd><AdminStatusBadge code={item.analysisStatusCode} label={item.analysisStatusCode} tone={tone(item.analysisStatusCode)} /></AdminTd>
          <AdminTd className="text-xs text-slate-600">완료 {item.completedComponentCount} · 부분 {item.partialComponentCount}<br />실패 {item.failedComponentCount} · 제외 {item.skippedComponentCount}</AdminTd>
          <AdminTd className="text-xs text-slate-600">원음 {formatNullable(item.rawAudioStatusCode)}<br />검수 {item.validationStatusCode}</AdminTd>
          <AdminTd><AdminButton variant="secondary" size="sm" onClick={() => navigate(`/admin/eafr-sound-events/${item.soundAnalysisEventId}`)}>보기</AdminButton></AdminTd>
        </AdminTr>)}
      </AdminTable>
      <AdminPagination currentPage={page} totalPages={totalPages} totalCount={totalElements} pageSize={size} onPageChange={setPage} itemLabel="건" />
    </AdminCard>
  </AdminPageLayout>;
};

export default EafrSoundEventsPage;
